import { forwardRef, memo, type HTMLAttributes } from 'react';
import { cva, type CVAVariantProps } from '../../utils/variants';
import { cn } from '../../utils/cn';
import type { BaseComponentProps } from '../../types';

/**
 * Varianti del Grid
 */
const gridVariants = cva(
  'grid',
  {
    variants: {
      cols: {
        1: 'grid-cols-1',
        2: 'grid-cols-1 sm:grid-cols-2',
        3: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
        4: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4',
        5: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5',
        6: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6',
        auto: 'grid-cols-[repeat(auto-fit,minmax(250px,1fr))]',
      },
      gap: {
        none: 'gap-0',
        xs: 'gap-1',
        sm: 'gap-2',
        md: 'gap-4',
        lg: 'gap-6',
        xl: 'gap-8',
      },
    },
    defaultVariants: {
      cols: 'auto',
      gap: 'md',
    },
  }
);

export interface GridProps
  extends HTMLAttributes<HTMLDivElement>,
    BaseComponentProps,
    CVAVariantProps<typeof gridVariants> {
  /**
   * Allinea gli item al centro
   */
  center?: boolean;
}

/**
 * Componente Grid
 * 
 * Sistema di griglia responsive con auto-fit/auto-fill.
 * 
 * @example
 * ```tsx
 * <Grid cols={3} gap="lg">
 *   <div>Item 1</div>
 *   <div>Item 2</div>
 *   <div>Item 3</div>
 * </Grid>
 * ```
 */
const GridComponent = forwardRef<HTMLDivElement, GridProps>(
  (
    {
      className,
      cols,
      gap,
      center,
      children,
      ...props
    },
    ref
  ) => {
    return (
      <div
        ref={ref}
        className={cn(
          gridVariants({ cols, gap }),
          center && 'place-items-center',
          className
        )}
        {...props}
      >
        {children}
      </div>
    );
  }
);

GridComponent.displayName = 'Grid';

export const Grid = memo(GridComponent);

